/**
 * E98.F7.T01 — o eixo "Contrato e apoio" do quadro da CCB.
 *
 * Reúne o que o motor grava na CCB do kit de veículos (E98.F4: bem financiado, campos
 * preenchidos depois da assinatura, emissão, informação prévia e documentos do envelope) e o
 * estágio dos documentos de apoio confirmados (E98.F5). O eixo só existe quando o resultado traz
 * um dos dois: os quadros dos demais bancos continuam com os sete eixos de sempre.
 *
 * `ApoioApuradoData` espelha `apps/api/types/exame_eletronico_apoio.py` (há teste de paridade).
 *
 * E107.F2 (ADR-0087): o manifesto de evidências do banco e o carimbo do tempo sobre ele entram no
 * mesmo eixo — são o que o banco declara do contrato. `ManifestoApuradoData` espelha
 * `apps/api/types/manifesto_de_evidencias.py` (há teste de paridade).
 */
import type { ItemVerificacaoData, ResultadoExameEletronicoData } from '@/lib/exame_eletronico';
import type { KitApuradoData, ResultadoComKitData } from '@/lib/exame_eletronico_kit';

export interface ApoioApuradoData {
  itens: ItemVerificacaoData[];
  diligencias: string[];
  /** E106.F3 — os documentos de identidade confirmados que a identidade ampliada leu. */
  documentos_confirmados?: (
    | 'documento_identidade'
    | 'cnh'
    | 'passaporte'
    | 'identidade_de_estrangeiro'
  )[];
}

export interface ManifestoApuradoData {
  itens: ItemVerificacaoData[];
  carimbo: {
    situacao: 'confere' | 'nao_confere' | 'autoridade_nao_confirmada' | 'ausente';
    instante?: string | null;
    autoridade?: string | null;
    politica?: string | null;
    motivo?: string | null;
  };
  hashes: {
    rotulo: string;
    sha256: string;
    tipo_da_peca?: string | null;
    nome_da_peca?: string | null;
  }[];
  /** E172.F9.C02 — as declarações de hash com valor que não é SHA-256, com o literal e o motivo. */
  hashes_invalidos?: {
    rotulo: string;
    valor: string;
    algoritmo?: string | null;
    motivo: 'valor_malformado' | 'algoritmo_nao_suportado';
    tipo_da_peca?: string | null;
  }[];
  hash_do_documento?: string | null;
}

export type ResultadoComContratoData = ResultadoComKitData & {
  apoio?: ApoioApuradoData | null;
  manifesto?: ManifestoApuradoData | null;
};

export interface ContratoDoQuadro {
  kit: KitApuradoData | null;
  apoio: ApoioApuradoData | null;
  manifesto: ManifestoApuradoData | null;
}

/** O que o eixo mostra; `null` quando o resultado não traz o kit, o apoio nem o manifesto. */
export function contratoDoResultado(
  resultado: ResultadoExameEletronicoData,
): ContratoDoQuadro | null {
  const comContrato = resultado as ResultadoComContratoData;
  const kit = comContrato.kit ?? null;
  const apoio = comContrato.apoio ?? null;
  const manifesto = comContrato.manifesto ?? null;
  return kit === null && apoio === null && manifesto === null ? null : { kit, apoio, manifesto };
}

/** Os itens do eixo, na ordem do quadro: os do kit, os de apoio e os do manifesto. */
export function itensDoContrato(contrato: ContratoDoQuadro): ItemVerificacaoData[] {
  return [
    ...(contrato.kit?.itens ?? []),
    ...(contrato.apoio?.itens ?? []),
    ...(contrato.manifesto?.itens ?? []),
  ];
}
